import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Observable, filter, map, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { CoreBaseApiCommandParams } from '@app/core/models/core-base-api.model';
import { CoreModuleName } from '@app/core/models/core-constant.model';
import { EidosConfigService } from '@common/config/eidos-config.service';
import { IEidosModuleConfiguration } from '@common/config/environment.interface';
import { DynamicApiResponse, IDynamicApiResponse } from '@common/models/dynamic-api-response.model';
import { CoreFormatService } from '@common/services/core-format.service';
import { EidosBaseApiService } from '@common/services/eidos-base-api.service';
import { EidosLogService } from '@common/services/eidos-log.service';
import { EidosSecurityService } from '@common/services/eidos-security.service';

import { IReservationModuleAPIConfiguration, ReservationUploadBlobResponse } from '@app/res-common/models/rescom.model';
import { IReservationApiAirLine, IReservationApiAirPnr, IReservationApiAirPnrHistory, IReservationApiAirResult, IReservationApiBookAirResultParams, IReservationApiDeleteAirPnrParams, IReservationApiDeleteFutureFlightParams, IReservationApiGetAirPnrHistoryParams, IReservationApiGetAirResultsParams, ResAirServiceOption, ReservationAirLine, ReservationAirPnrHistory, ReservationAirResult, ReservationApiRightRezParams, ReservationApiRightRezResponse } from '../models/res-air.model';
import { IResApiManageOwnArrangementParams, ResFlightOwnArrangement } from '../models/res-booking.model';

@Injectable({
  providedIn: 'root'
})
export class ReservationAirApiService extends EidosBaseApiService {

  constructor(
    public configService: EidosConfigService,
    public eidosSecurityService: EidosSecurityService,
    public http: HttpClient,
    public eidosLogService: EidosLogService,
    public domSanitizer: DomSanitizer,
    public coreFormatService: CoreFormatService
  ) {
    super(coreFormatService, configService, eidosLogService, eidosSecurityService, http);
    this.configService.currentModulesConfig
      .pipe(
        filter(
          (modules: Array<IEidosModuleConfiguration>) => !!modules.find((module) => module.moduleName === CoreModuleName.Reservation)
        )
      )
      .subscribe((modules) => {
        const module = modules.find((module) => module.moduleName === CoreModuleName.Reservation);
        if (module) {
          const reservationModuleAPI = module.API as IReservationModuleAPIConfiguration;
          this.urlAPI = reservationModuleAPI.url;
          this.staticUrlAPI = reservationModuleAPI.staticUrl;
          this.pluginUrlAPI = reservationModuleAPI.pluginUrl;
          this.apiVersion = reservationModuleAPI.version;
          if (reservationModuleAPI.headers) {
            this.headers = {
              ...reservationModuleAPI.headers,
              ...this.headers
            };
          }
        }
      });
  }

  bookAirResult(params: IReservationApiBookAirResultParams): Observable<{ pnr: IReservationApiAirPnr, Error?: string }> {
    return this.sendCommand(new CoreBaseApiCommandParams({
      url: 'gmt/bookpnr',
      data: params,
      type: 'POST',
      customBaseUrl: this.pluginUrlAPI
    })).pipe(
      catchError(({ error: { Error } }) => {
        return of({ pnr: {} as IReservationApiAirPnr, Error })
      })
    )
  }

  deleteAirInternalFlight(params: IReservationApiDeleteAirPnrParams): Observable<string> {
    return this.postDynamicAPI('air/internalFlightDelete', params).pipe(map<{ Error?: string }, string>((response) => response?.Error || ''))
  }

  deleteFutureFlight(params: IReservationApiDeleteFutureFlightParams): Observable<any> {
    return this.getDynamicAPI('bookingAir/deleteFutureFlight', params).pipe(
      map<DynamicApiResponse, any>((response) => {
        return response.body
      })
    );
  }

  deletePnrDocument(flightHeaderId: number) {
    return this.sendCommand(new CoreBaseApiCommandParams({
      url: 'air/registerPnrDoc',
      data: {
        FlightHeaderId: flightHeaderId
      },
      type: 'POST',
      useVersion: true
    })).pipe(
      map<IDynamicApiResponse, DynamicApiResponse>((response) => new DynamicApiResponse(response)),
      map<DynamicApiResponse, boolean>((response) => response.errors.length === 0)
    );
  }

  /**
   * upload file on blob Azure
   *
   * @param {IReservationApiUploadBlobParameters} data*
   * @memberof ReservationAirApiService
   */
  public downBlobFile(guid: string) {
    return `${this.config?.urlAPI}/documents/document-blob?FileGuid=${guid}`;
  }

  getAirLines(): Observable<IReservationApiAirLine[]> {
    return this.callDynamicAPI('airlines/select', 'GET').pipe(
      map<DynamicApiResponse, IReservationApiAirLine[]>((response) =>
        response.body.map((al: any) => new ReservationAirLine(al))
      )
    );
  }

  getAirPnrHistory(params: IReservationApiGetAirPnrHistoryParams): Observable<ReservationAirPnrHistory[]> {
    return this.sendCommand(new CoreBaseApiCommandParams({
      url: 'gmt/pnrhistory',
      data: params,
      type: 'GET',
      customBaseUrl: this.pluginUrlAPI
    })).pipe(
      map<IReservationApiAirPnrHistory[], Array<ReservationAirPnrHistory>>((response) => {
        return response.map((pnr: any) => new ReservationAirPnrHistory(pnr));
      })
    );
  }

  getAirResults(params: IReservationApiGetAirResultsParams): Observable<{ itineraries: ReservationAirResult[], Error?: string }> {

    const commandParams = new CoreBaseApiCommandParams({
      url: 'gmt/searchavailability',
      data: params,
      type: 'POST',
      customBaseUrl: this.pluginUrlAPI
    });

    return this.sendCommand(commandParams).pipe(
      map<{ itineraries?: IReservationApiAirResult[], Error?: string }, { itineraries: ReservationAirResult[], Error?: string }>((response) => {
        return {
          itineraries: response.itineraries?.map((itinerary: any) => new ReservationAirResult(itinerary)) || [],
          Error: response.Error
        };
      }),
      catchError(({ error: { Error } }) => {
        return of({ itineraries: [], Error })
      })
    );
  }

  getAirlines({ AirLine, AirLineCod }: { AirLine?: string, AirLineCod?: string }) {
    return this.callDynamicAPI('lookup/getAirlines', { AirLine, AirLineCod }).pipe(
      map<DynamicApiResponse, any>((response) => {
        return response.body;
      })
    )
  }

  getAirports({ IataCod, AirName }: { IataCod?: string, AirName?: string }) {
    return this.callDynamicAPI('lookup/getAirports', { IataCod, AirName }).pipe(
      map<DynamicApiResponse, any>((response) => {
        return response.body;
      })
    )
  }

  getFlightServiceOption(bkgID: number): Observable<ResAirServiceOption[]> {
    return this.callDynamicAPI('air/getFlightServiceOption', { BkgID: bkgID }).pipe(
      map<DynamicApiResponse, ResAirServiceOption[]>(
        (response) => response.body.map((data: any) => new ResAirServiceOption(data))
      ));
  }

  getOwnArrangement(OwnArrangementID: number) {
    return this.callDynamicAPI('guest/getOwnArrangement', { OwnArrangementID }).pipe(
      map<DynamicApiResponse, any>((response) => {
        return new ResFlightOwnArrangement(response.body[0]);
      })
    )
  }

  getRightRezUrl(params: ReservationApiRightRezParams): Observable<ReservationApiRightRezResponse> {
    const commandParams = new CoreBaseApiCommandParams({
      url: 'oem/rightrez/book',
      data: params,
      type: 'POST',
      customBaseUrl: this.pluginUrlAPI
    });

    return this.sendCommand(commandParams).pipe(

      map<DynamicApiResponse, ReservationApiRightRezResponse>((response) => new ReservationApiRightRezResponse(response)),
      catchError(err => {
        return of(new ReservationApiRightRezResponse(err))
      })
    );
  }

  manageOwnArrangement(params: IResApiManageOwnArrangementParams[]) {
    return this.postDynamicAPI('guest/manageOwnArrangement', { OwnArrangement: params }).pipe(
      map<DynamicApiResponse, any>((response) => {
        return response.body;
      })
    )
  }

  uploadPnrDocument(params: ReservationUploadBlobResponse, flightHeaderId: number) {
    return this.sendCommand(new CoreBaseApiCommandParams({
      url: 'air/registerPnrDoc',
      data: {
        FlightHeaderId: flightHeaderId,
        FileID: params.fileId,
        FileGUID: params.fileGuid,
        FileName: params.fileName
      },
      type: 'POST',
      useVersion: true
    })).pipe(
      map<IDynamicApiResponse, DynamicApiResponse>((response) => new DynamicApiResponse(response)),
      map<DynamicApiResponse, boolean>((response) => response.errors.length === 0)
    );
  }
}
